import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API, API_ENDPOINTS, ApiResponse } from "@/constants/api";
import { getAccessToken, setAccessToken, clearTokens } from "./tokenUtils";
import logger from "./logger";

// Axios 인스턴스 생성
const api = axios.create({
    baseURL: API.BASE_URL,
    headers: {
        Accept: "application/json"
    },
    withCredentials: true, // CORS 요청에서 쿠키 전송을 위해 필요
    timeout: 10000 // 10초 타임아웃 설정
});

// 요청 인터셉터 설정
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // refresh나 로그인 요청이 아닌 경우에만 Authorization 헤더 추가
        if (
            !config.url?.includes(API_ENDPOINTS.REFRESH) &&
            !config.url?.includes(API_ENDPOINTS.LOGIN) &&
            !config.url?.includes(API_ENDPOINTS.SNS_LOGIN)
        ) {
            const token = getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

interface ExtendedInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

/**
 * 로그아웃 처리를 수행합니다.
 */
const handleLogout = async () => {
    try {
        // 로그아웃 API 호출 (쿠키의 Refresh Token이 자동으로 전송됨)
        await api.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
        logger.error("로그아웃 처리 중 에러 발생:", error);
    } finally {
        // 로컬의 토큰 정보 삭제
        clearTokens();
        // 로그인 페이지로 이동
        window.location.href = "/auth/login";
    }
};

// 응답 인터셉터 설정
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiResponse>) => {
        const originalRequest = error.config as ExtendedInternalAxiosRequestConfig;

        // Access Token 만료 에러 처리 (401 에러)
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // Refresh Token은 쿠키로 자동 전송됨
                const response = await api.post<ApiResponse<{ accessToken: string }>>(API_ENDPOINTS.REFRESH);

                const newAccessToken = response.data.data.accessToken;
                setAccessToken(newAccessToken);

                // 원래 요청의 Authorization 헤더 업데이트
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                }

                // 원래 요청 재시도
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh Token이 만료되었거나 유효하지 않은 경우
                logger.error("토큰 갱신 실패:", refreshError);
                await handleLogout();
                return Promise.reject(refreshError);
            }
        }

        // 500 에러 처리
        if (error.response?.status === 500) {
            logger.error("서버 오류가 발생했습니다:", error);
            // 여기서 추가 로직 없이 바로 reject하여 무한 루프 방지
        }

        // 타임아웃 에러 처리
        if (error.code === "ECONNABORTED" && error.message && error.message.includes("timeout")) {
            logger.error("요청 시간이 초과되었습니다:", error);
            // 필요시 사용자에게 타임아웃 알림 표시 로직 추가
        }

        return Promise.reject(error);
    }
);

export default api;
